import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { formatDate, formatDay } from '../lib/formatUtils';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Clock, Loader2, CheckCircle2, AlertTriangle, LogIn, LogOut, CalendarDays, RotateCcw, FileSpreadsheet, MapPin, Pencil, Users, PenSquare } from 'lucide-react';
import { RegularizationRequestsTab } from '../components/attendance/RegularizationRequestsTab';
import { exportToExcel } from '../lib/exportUtils';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../components/ui/hover-card';
import { useAuth } from '../context/AuthContext';
import { usePageSize } from '../context/PageSizeContext';
import { Pagination } from '../components/Pagination';
import { EmployeeLink } from '../components/links/EntityLinks';
import { PersonAvatar } from '../components/PersonAvatar';
import { toast } from 'sonner';

// User-facing label + badge styling for every attendance status the system
// supports. Keep in sync with backend ATTENDANCE_STATUSES enum (hr.py).
const STATUS_CONFIG = {
  present:             { label: 'Present',           cls: 'bg-emerald-500/10 text-emerald-500' },
  absent:              { label: 'Absent',            cls: 'bg-red-500/10 text-red-500' },
  paid_leave:          { label: 'Paid Leave',        cls: 'bg-blue-500/10 text-blue-500' },
  unpaid_leave:        { label: 'Unpaid Leave',      cls: 'bg-orange-500/10 text-orange-500' },
  half_day_paid_leave: { label: 'Half-Day (Paid)',   cls: 'bg-purple-500/10 text-purple-500' },
  holiday:             { label: 'Holiday',           cls: 'bg-indigo-500/10 text-indigo-500' },
  weekly_off:          { label: 'Weekly Off',        cls: 'bg-zinc-500/10 text-zinc-500' },
  // Legacy values kept for backwards-compat with already-migrated rows
  late:                { label: 'Late',              cls: 'bg-amber-500/10 text-amber-500' },
  on_leave:            { label: 'On Leave',          cls: 'bg-blue-500/10 text-blue-500' },
  half_day:            { label: 'Half-Day',          cls: 'bg-purple-500/10 text-purple-500' },
};

// Local-date helpers — `toISOString()` would convert to UTC and yield the
// previous day for users east of UTC after midnight local time, hiding
// freshly-created attendance rows. We format from local components instead.
const _isoLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const todayISO = () => _isoLocal(new Date());
const isoNDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return _isoLocal(d);
};

// Helpers — current month bounds.
const monthStartISO = () => {
  const d = new Date();
  return _isoLocal(new Date(d.getFullYear(), d.getMonth(), 1));
};
const monthEndISO = (y, m /* 1-12 */) => {
  // Last day of month m in year y — day 0 of the next month.
  return _isoLocal(new Date(y, m, 0));
};
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export const AttendancePage = () => {
  const [activeTab, setActiveTab] = useState('daily'); // 'daily' | 'overview'
  const [attendance, setAttendance] = useState([]);
  const [attendanceTotal, setAttendanceTotal] = useState(0);
  const [overview, setOverview] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [locationsByKey, setLocationsByKey] = useState({});
  const [userToEmp, setUserToEmp] = useState({});
  const [loading, setLoading] = useState(true);
  // Default to current month so both tabs have meaningful data on first load.
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const { pageSize } = usePageSize();
  // Override modal — for admins / HR to manually set attendance.
  const { hasPermission } = useAuth();
  const canOverride = hasPermission('attendance.override') || hasPermission('*');
  const [overrideDialog, setOverrideDialog] = useState({ open: false, row: null });
  const [overrideForm, setOverrideForm] = useState({ status: 'present', check_in: '', check_out: '', note: '' });
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const setFilterAndResetPage = (setter) => (value) => { setter(value); setPage(1); };

  // (no separate useEffect for page reset — filter setters are wrapped above.)

  // Daily Entries: server-side paginated fetch.
  useEffect(() => {
    if (activeTab !== 'daily') return;
    fetchDaily();
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, employeeFilter, page, pageSize, activeTab]);

  // Overview: fetched whenever filters change (regardless of active tab) so
  // the top stat cards stay accurate even on the Daily Entries tab.
  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, employeeFilter]);

  // Active employees only — dropdown source. Fetched once.
  useEffect(() => {
    api.get('/employees?status=active&limit=500').then(res => {
      if (res.data?.success) {
        const list = (res.data.data || []).map(e => ({
          id: e.user_id || e.id,
          internal_id: e.id,
          name: `${e.first_name || ''} ${e.last_name || ''}`.trim(),
          first_name: e.first_name || '',
          last_name: e.last_name || '',
          profile_picture: e.profile_picture || '',
        })).filter(e => e.id && e.name);
        setAllEmployees(list);
      }
    }).catch(() => {});
  }, []);

  const fetchDaily = () => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page), limit: String(pageSize),
      sort_by: 'date', sort_order: 'desc',
    });
    if (dateFrom) params.set('date_gte', dateFrom);
    if (dateTo) params.set('date_lte', dateTo);
    if (employeeFilter && employeeFilter !== 'all') params.set('employee_id', employeeFilter);
    api.get(`/attendance?${params.toString()}`)
      .then(res => {
        if (res.data.success) {
          setAttendance(res.data.data);
          setAttendanceTotal(res.data.total || 0);
        }
      })
      .catch(() => { setAttendance([]); setAttendanceTotal(0); })
      .finally(() => setLoading(false));
  };

  const fetchOverview = () => {
    if (!dateFrom || !dateTo) return;
    setOverviewLoading(true);
    const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
    if (employeeFilter && employeeFilter !== 'all') params.set('employee_id', employeeFilter);
    api.get(`/attendance/overview?${params.toString()}`)
      .then(res => { if (res.data.success) setOverview(res.data.data || []); })
      .catch(() => setOverview([]))
      .finally(() => setOverviewLoading(false));
  };

  const fetchLocations = async () => {
    if (!dateFrom || !dateTo) { setLocationsByKey({}); setUserToEmp({}); return; }
    try {
      const res = await api.get(`/auth/locations/range?date_from=${dateFrom}&date_to=${dateTo}&limit=500`);
      if (res.data.success) {
        // Group ALL locations of a (user, day) into an array, sorted earliest → latest
        const idx = {};
        (res.data.data || []).forEach(loc => {
          const key = `${loc.user_id}|${loc.date}`;
          if (!idx[key]) idx[key] = [];
          idx[key].push(loc);
        });
        // Sort each bucket by captured_at ascending so the first is the earliest
        Object.values(idx).forEach(arr => arr.sort((a, b) => (a.captured_at || '').localeCompare(b.captured_at || '')));
        setLocationsByKey(idx);
        setUserToEmp(res.data.user_to_employee || {});
      }
    } catch {
      setLocationsByKey({});
      setUserToEmp({});
    }
  };

  const employees = useMemo(() => {
    // Prefer the full employee directory (so the dropdown lists everyone,
    // even employees with no attendance in the loaded page). Fall back to
    // names seen in the attendance feed. Dedupe by id AND by name (legacy
    // dumps sometimes carry duplicate employees with different user_ids).
    const dedupe = (list) => {
      const seenIds = new Set();
      const seenNames = new Set();
      const out = [];
      for (const e of list) {
        const nameKey = e.name.toLowerCase();
        if (seenIds.has(e.id) || seenNames.has(nameKey)) continue;
        seenIds.add(e.id);
        seenNames.add(nameKey);
        out.push(e);
      }
      return out;
    };
    if (allEmployees.length > 0) {
      return dedupe([...allEmployees].sort((a, b) => a.name.localeCompare(b.name)));
    }
    const seen = new Map();
    attendance.forEach(a => {
      if (a.employee_id && !seen.has(a.employee_id)) {
        seen.set(a.employee_id, a.employee_name || 'Unknown');
      }
    });
    return dedupe(Array.from(seen, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name)));
  }, [allEmployees, attendance]);

  // Quick lookup map keyed by user_id → employee record (avatar + name + id).
  const employeeByUser = useMemo(
    () => Object.fromEntries(allEmployees.map(e => [e.id, e])),
    [allEmployees]
  );

  // Daily Entries table now reflects the server response 1:1 — no client
  // filtering. Date/employee/page params are sent to the backend so the
  // total + stat counts stay accurate at any page size.
  const filtered = attendance;

  const resetFilters = () => {
    setDateFrom(monthStartISO());
    setDateTo(todayISO());
    setEmployeeFilter('all');
  };

  const getStatusBadge = (status) => {
    const c = STATUS_CONFIG[status] || STATUS_CONFIG.absent;
    return (
      <Badge className={`${c.cls} flex items-center`}>
        {status === 'present' && <CheckCircle2 className="h-3 w-3 mr-1" />}
        {status === 'late' && <AlertTriangle className="h-3 w-3 mr-1" />}
        {c.label}
      </Badge>
    );
  };

  const formatHours = (h) => {
    if (!h && h !== 0) return '-';
    const hrs = Math.floor(h);
    const mins = Math.round((h - hrs) * 60);
    return `${hrs}h ${mins}m`;
  };

  const openOverride = (row) => {
    setOverrideForm({
      status: row.status || 'present',
      check_in: row.check_in || '',
      check_out: row.check_out || '',
      note: row.note || '',
    });
    setOverrideDialog({ open: true, row });
  };

  const handleOverrideSave = async () => {
    const { row } = overrideDialog;
    if (!row) return;
    setOverrideSaving(true);
    try {
      await api.put(`/attendance/override/${row.employee_id}/${row.date}`, overrideForm);
      toast.success('Attendance updated');
      setOverrideDialog({ open: false, row: null });
      fetchDaily();
      fetchOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setOverrideSaving(false);
    }
  };

  // Always-accurate aggregate counts — derived from the per-employee
  // overview (which itself uses server-side, range-wide math). Avoids the
  // "stats reflect only the current page" bug.
  // NOTE (Feb 2026): widget parity with the employee "My Attendance"
  // portal — "Work Days" and "Leaves" must sum the same status buckets
  // on both sides. `paid_leave` is a leave (not an absent), so it feeds
  // both `records` (Work Days) and the Leaves widget.
  const aggregate = useMemo(() => {
    const init = { records: 0, present: 0, absent: 0, leave: 0, half_day: 0, paid_leave: 0, week_off: 0, holiday: 0 };
    overview.forEach(c => {
      init.records += (c.present + c.absent + c.leave + c.half_day + c.late + (c.paid_leave || 0));
      init.present += c.present; init.absent += c.absent; init.leave += c.leave;
      init.half_day += c.half_day; init.paid_leave += c.paid_leave;
      init.week_off += c.week_off; init.holiday += c.holiday;
    });
    return init;
  }, [overview]);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (employeeFilter && employeeFilter !== 'all') params.set('employee_id', employeeFilter);
      const res = await api.get(`/attendance/export-rows?${params.toString()}`);
      const rows = res.data?.data || [];
      if (!rows.length) { toast.info('No rows to export for the current filters'); return; }
      exportToExcel(rows.map(a => {
        const locs = locationsByKey[`${a.employee_id}|${a.date}`] || [];
        const loc = locs[0] || {};
        const addr = loc.formatted_address || [loc.area, loc.city, loc.state, loc.country].filter(Boolean).join(', ');
        return {
          employee_name: a.employee_name, date: a.date,
          check_in: a.check_in, check_out: a.check_out, total_hours: a.total_hours,
          status: a.status, location: addr || '',
          coordinates: loc.latitude && loc.longitude ? `${loc.latitude},${loc.longitude}` : '',
        };
      }), [
        { key: 'employee_name', header: 'Employee' }, { key: 'date', header: 'Date' },
        { key: 'check_in', header: 'Check In' }, { key: 'check_out', header: 'Check Out' },
        { key: 'total_hours', header: 'Hours' }, { key: 'status', header: 'Status' },
        { key: 'location', header: 'Location' }, { key: 'coordinates', header: 'Coordinates' },
      ], `Attendance_${dateFrom}_to_${dateTo}`);
      toast.success(`Exported ${rows.length} rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  const handleExportOverview = () => {
    if (!overview.length) { toast.info('No employees to export'); return; }
    exportToExcel(overview, [
      { key: 'name', header: 'Employee' }, { key: 'designation', header: 'Designation' },
      { key: 'total_days', header: 'Total Days' }, { key: 'present', header: 'Present' },
      { key: 'absent', header: 'Absent' }, { key: 'half_day', header: 'Half-Day' },
      { key: 'leave', header: 'Leave' }, { key: 'paid_leave', header: 'Paid Leave' },
      { key: 'week_off', header: 'Week Off' }, { key: 'holiday', header: 'Holiday' },
    ], `Attendance_Overview_${dateFrom}_to_${dateTo}`);
  };

  const presentCount = aggregate.present;
  const lateCount = 0; // legacy stat — overview rolls late into present
  const absentCount = aggregate.absent;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Attendance</h1>
          <p className="text-muted-foreground">Track attendance across employees and date ranges</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={activeTab === 'daily' ? handleExportExcel : handleExportOverview}
            disabled={exporting}
            data-testid="attendance-export-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-3 items-end flex-wrap">
            {/* Month quick-picker — sets From = 1st, To = last day (or today
                if the picked month is the current one). Mirrors the pattern
                used elsewhere in the ERP so operators aren't guessing dates. */}
            <div className="min-w-[140px]">
              <label className="text-xs text-muted-foreground mb-1.5 block">Month</label>
              <Select
                value={(() => {
                  // Auto-detect when From/To describe a full calendar month
                  // so the dropdown stays in sync with manual date edits.
                  const f = new Date(dateFrom); const t = new Date(dateTo);
                  if (isNaN(f) || isNaN(t)) return 'custom';
                  const monthEnd = new Date(f.getFullYear(), f.getMonth() + 1, 0);
                  const today = new Date(); today.setHours(0,0,0,0);
                  const expectedTo = monthEnd > today ? today : monthEnd;
                  if (f.getDate() === 1
                    && f.getFullYear() === t.getFullYear()
                    && f.getMonth() === t.getMonth()
                    && _isoLocal(t) === _isoLocal(expectedTo)) {
                    return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2,'0')}`;
                  }
                  return 'custom';
                })()}
                onValueChange={(v) => {
                  if (v === 'custom') return;
                  const [yy, mm] = v.split('-').map(Number);
                  const start = _isoLocal(new Date(yy, mm - 1, 1));
                  const endOfMonth = new Date(yy, mm, 0);
                  const today = new Date(); today.setHours(0,0,0,0);
                  const end = _isoLocal(endOfMonth > today ? today : endOfMonth);
                  setDateFrom(start); setDateTo(end); setPage(1);
                }}
              >
                <SelectTrigger data-testid="attendance-month-filter"><SelectValue placeholder="Pick a month" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="custom">Custom range</SelectItem>
                  {(() => {
                    // Emit the trailing 24 months (current → 23 back).
                    const now = new Date();
                    const items = [];
                    for (let i = 0; i < 24; i++) {
                      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                      const y = d.getFullYear(), m = d.getMonth() + 1;
                      items.push({ value: `${y}-${String(m).padStart(2,'0')}`, label: `${MONTHS[m - 1]} ${y}` });
                    }
                    return items.map(it => <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>);
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-muted-foreground mb-1.5 block">From</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                data-testid="attendance-date-from"
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-muted-foreground mb-1.5 block">To</label>
              <Input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                data-testid="attendance-date-to"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground mb-1.5 block">Employee</label>
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger data-testid="attendance-employee-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={resetFilters}
              data-testid="attendance-reset-filters"
            >
              <RotateCcw className="h-4 w-4 mr-1" />Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Clock className="h-5 w-5 text-primary" /></div>
              <div><p className="text-2xl font-bold" data-testid="attendance-total">{aggregate.records}</p><p className="text-xs text-muted-foreground">Work Days</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-emerald-500" /></div>
              <div><p className="text-2xl font-bold" data-testid="attendance-present">{presentCount}</p><p className="text-xs text-muted-foreground">Present Days</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-amber-500" /></div>
              <div><p className="text-2xl font-bold" data-testid="attendance-leave">{aggregate.leave + (aggregate.paid_leave || 0)}</p><p className="text-xs text-muted-foreground">Leaves</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center"><Clock className="h-5 w-5 text-red-500" /></div>
              <div><p className="text-2xl font-bold" data-testid="attendance-absent">{absentCount}</p><p className="text-xs text-muted-foreground">Absent Days</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4" data-testid="attendance-tabs">
        <TabsList>
          <TabsTrigger value="daily" data-testid="attendance-tab-daily"><CalendarDays className="h-4 w-4 mr-2" />Daily Entries</TabsTrigger>
          <TabsTrigger value="overview" data-testid="attendance-tab-overview"><Users className="h-4 w-4 mr-2" />Employee Overview</TabsTrigger>
          <TabsTrigger value="regularization" data-testid="attendance-tab-regularization"><PenSquare className="h-4 w-4 mr-2" />Regularization Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="space-y-4">

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <CalendarDays className="h-12 w-12 mb-4 opacity-50" />
              <p>No attendance records match the selected filters</p>
            </div>
          ) : (
            <Table data-testid="attendance-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Check In</TableHead>
                  <TableHead>Check Out</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Location</TableHead>
                  {canOverride && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(a => {
                  // attendance.employee_id is the user_id; locations also key by user_id
                  const locs = locationsByKey[`${a.employee_id}|${a.date}`] || [];
                  const loc = locs[0];
                  const addr = loc && (loc.formatted_address || [loc.area, loc.city, loc.state, loc.country].filter(Boolean).join(', '));
                  // Resolve actual employees.id for profile link
                  const actualEmpId = userToEmp[a.employee_id]?.employee_id;
                  return (
                    <TableRow key={a.id} data-testid={`attendance-row-${a.id}`}>
                      <TableCell className="font-medium">
                        {(() => {
                          const empRec = employeeByUser[a.employee_id];
                          const empInternalId = empRec?.internal_id || actualEmpId;
                          const [fn, ...rest] = (a.employee_name || '').split(' ');
                          return (
                            <div className="flex items-center gap-2">
                              {empInternalId ? (
                                <Link to={`/employees/${empInternalId}`} className="flex-shrink-0">
                                  <PersonAvatar
                                    firstName={empRec?.first_name || fn}
                                    lastName={empRec?.last_name || rest.join(' ')}
                                    picture={empRec?.profile_picture}
                                    seed={a.employee_id}
                                    size="sm"
                                  />
                                </Link>
                              ) : (
                                <PersonAvatar
                                  firstName={empRec?.first_name || fn}
                                  lastName={empRec?.last_name || rest.join(' ')}
                                  picture={empRec?.profile_picture}
                                  seed={a.employee_id}
                                  size="sm"
                                />
                              )}
                              {empInternalId ? (
                                <Link
                                  to={`/employees/${empInternalId}`}
                                  className="text-primary hover:underline transition-colors"
                                  data-testid={`attendance-employee-link-${empInternalId}`}
                                >
                                  {a.employee_name}
                                </Link>
                              ) : (
                                <span>{a.employee_name}</span>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {formatDate(a.date)}
                        {formatDay(a.date) && (
                          <span className="ml-1 text-[10px] text-muted-foreground" data-testid={`att-day-${a.date}`}>
                            · {formatDay(a.date)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {a.check_in ? (
                          <div className="flex items-center gap-1"><LogIn className="h-3 w-3 text-emerald-500" />{a.check_in}</div>
                        ) : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        {a.check_out ? (
                          <div className="flex items-center gap-1"><LogOut className="h-3 w-3 text-blue-500" />{a.check_out}</div>
                        ) : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{formatHours(a.total_hours)}</TableCell>
                      <TableCell>{getStatusBadge(a.status)}</TableCell>
                      <TableCell className="text-sm max-w-[260px]">
                        {loc ? (
                          <div className="flex items-center gap-1.5 min-w-0">
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-start gap-1.5 hover:text-primary hover:underline transition-colors min-w-0"
                              data-testid={`attendance-location-link-${a.id}`}
                              title={loc.ip_address ? `IP: ${loc.ip_address}` : 'No IP recorded'}
                            >
                              <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-rose-500" />
                              <span className="truncate">{addr || `${Number(loc.latitude).toFixed(4)}, ${Number(loc.longitude).toFixed(4)}`}</span>
                            </a>
                            {loc.ip_address && (
                              <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap" data-testid={`attendance-ip-${a.id}`}>· {loc.ip_address}</span>
                            )}
                            {locs.length > 1 && (
                              <HoverCard openDelay={120}>
                                <HoverCardTrigger asChild>
                                  <button
                                    type="button"
                                    className="text-[10px] font-semibold whitespace-nowrap px-1.5 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-rose-500/20 transition-colors flex-shrink-0"
                                    data-testid={`attendance-location-more-${a.id}`}
                                  >
                                    +{locs.length - 1} more
                                  </button>
                                </HoverCardTrigger>
                                <HoverCardContent align="end" className="w-80 p-0 max-h-[320px] overflow-y-auto" data-testid={`attendance-location-popover-${a.id}`}>
                                  <div className="px-3 py-2 border-b border-border bg-muted/30">
                                    <p className="text-xs font-semibold text-foreground">Login locations</p>
                                    <p className="text-[10px] text-muted-foreground">
                                      {a.employee_name} • {formatDate(a.date)}
                                      {formatDay(a.date) && <span className="ml-1 opacity-80">· {formatDay(a.date)}</span>}
                                    </p>
                                  </div>
                                  <div className="divide-y divide-border/50">
                                    {locs.map((l, i) => {
                                      const lAddr = l.formatted_address || [l.area, l.city, l.state, l.country].filter(Boolean).join(', ');
                                      const time = l.captured_at ? new Date(l.captured_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';
                                      return (
                                        <a
                                          key={l.id || i}
                                          href={`https://www.google.com/maps/search/?api=1&query=${l.latitude},${l.longitude}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-start gap-2 px-3 py-2 hover:bg-muted/40 transition-colors"
                                        >
                                          <span className="text-[10px] font-mono text-muted-foreground tabular-nums mt-0.5 flex-shrink-0 w-12">{time}</span>
                                          <MapPin className="h-3 w-3 mt-1 flex-shrink-0 text-rose-500" />
                                          <span className="text-xs flex-1 min-w-0 break-words">
                                            {lAddr || `${Number(l.latitude).toFixed(5)}, ${Number(l.longitude).toFixed(5)}`}
                                            {l.accuracy_meters != null && (
                                              <span className="ml-1 text-[10px] text-muted-foreground">±{Math.round(l.accuracy_meters)}m</span>
                                            )}
                                          </span>
                                        </a>
                                      );
                                    })}
                                  </div>
                                </HoverCardContent>
                              </HoverCard>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      {canOverride && (
                        <TableCell>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => openOverride(a)}
                            data-testid={`attendance-edit-${a.id}`}
                            title="Override attendance"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Pagination
        page={page}
        total={attendanceTotal}
        pageSize={pageSize}
        onPageChange={setPage}
      />
      </TabsContent>

      <TabsContent value="overview" className="space-y-4" data-testid="attendance-overview-tab">
        {overviewLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : overview.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No active employees match the current filter.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="attendance-overview-grid">
            {overview.map(c => (
              <Card key={c.employee_id} data-testid={`overview-card-${c.employee_id}`} className="border-border/60">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Link to={`/employees/${c.internal_id}`} className="flex-shrink-0" title={`Open ${c.name}'s profile`}>
                      <PersonAvatar
                        firstName={(c.name || '').split(' ')[0]}
                        lastName={(c.name || '').split(' ').slice(1).join(' ')}
                        picture={c.profile_picture}
                        seed={c.employee_id}
                        size="md"
                      />
                    </Link>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate" title={c.name}>
                        <EmployeeLink id={c.internal_id} name={c.name} testId={`overview-name-${c.employee_id}`} />
                      </p>
                      {c.designation && <p className="text-xs text-muted-foreground truncate" title={c.designation}>{c.designation}</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {c.attendance_exempt && (
                        <Badge className="bg-violet-500/10 text-violet-400 border border-violet-500/30 text-[10px]" title="Attendance-exempt — payroll assumes full month" data-testid={`overview-exempt-${c.employee_id}`}>Exempt</Badge>
                      )}
                      <Badge variant="outline" className="text-[10px]">{c.total_days}d</Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <Metric label="Present" value={c.present} tone="emerald" />
                    <Metric label="Absent" value={c.absent} tone="red" />
                    <Metric label="Half-Day" value={c.half_day} tone="amber" />
                    <Metric label="Leave" value={c.leave} tone="amber" />
                    <Metric label="Paid Leave" value={c.paid_leave} tone="sky" />
                    <Metric label="Week Off" value={c.week_off} tone="muted" />
                    <Metric label="Holiday" value={c.holiday} tone="muted" />
                    <Metric label="Late" value={c.late} tone="amber" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="regularization" className="space-y-4" data-testid="attendance-regularization-tab">
        <RegularizationRequestsTab />
      </TabsContent>
      </Tabs>

      {/* Manual attendance override (admin / HR only) */}
      <Dialog
        open={overrideDialog.open}
        onOpenChange={(o) => !o && setOverrideDialog({ open: false, row: null })}
      >
        <DialogContent className="max-w-md" data-testid="attendance-override-dialog">
          <DialogHeader>
            <DialogTitle>Override Attendance</DialogTitle>
            <DialogDescription>
              Manually update <strong>{overrideDialog.row?.employee_name}</strong>'s attendance for{' '}
              <strong>{overrideDialog.row?.date ? `${formatDate(overrideDialog.row.date)}${formatDay(overrideDialog.row.date) ? ' · ' + formatDay(overrideDialog.row.date) : ''}` : ''}</strong>.
              The change is logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2 space-y-1">
              <Label>Status</Label>
              <Select
                value={overrideForm.status}
                onValueChange={(v) => setOverrideForm(f => ({ ...f, status: v }))}
              >
                <SelectTrigger data-testid="override-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[
                    'present', 'absent', 'paid_leave', 'unpaid_leave',
                    'half_day_paid_leave', 'holiday', 'weekly_off',
                  ].map(s => (
                    <SelectItem key={s} value={s} data-testid={`override-status-option-${s}`}>
                      {STATUS_CONFIG[s]?.label || s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Check In</Label>
              <Input
                type="time"
                value={overrideForm.check_in || ''}
                onChange={(e) => setOverrideForm(f => ({ ...f, check_in: e.target.value }))}
                data-testid="override-check-in"
              />
            </div>
            <div className="space-y-1">
              <Label>Check Out</Label>
              <Input
                type="time"
                value={overrideForm.check_out || ''}
                onChange={(e) => setOverrideForm(f => ({ ...f, check_out: e.target.value }))}
                data-testid="override-check-out"
              />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Reason / Note</Label>
              <Textarea
                rows={2}
                value={overrideForm.note}
                onChange={(e) => setOverrideForm(f => ({ ...f, note: e.target.value }))}
                placeholder="e.g. Employee forgot to clock in — confirmed via Slack"
                data-testid="override-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOverrideDialog({ open: false, row: null })}
              disabled={overrideSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleOverrideSave}
              disabled={overrideSaving}
              data-testid="override-save-btn"
            >
              {overrideSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Tiny per-stat tile used inside the per-employee overview cards.
const Metric = ({ label, value, tone }) => {
  const toneMap = {
    emerald: 'text-emerald-500',
    red: 'text-red-500',
    amber: 'text-amber-500',
    sky: 'text-sky-500',
    muted: 'text-muted-foreground',
  };
  return (
    <div className="rounded-md bg-muted/30 py-1.5 px-1">
      <p className={`text-base font-bold tabular-nums ${toneMap[tone] || 'text-foreground'}`}>{value}</p>
      <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
};
